import { Controller } from "@hotwired/stimulus";

/**
 * Pill Slide Controller
 *
 * PillTabsComponent attaches this to every track. A click slides the lit pill
 * to the one clicked, for all three shapes: links and buttons (lit by
 * `data-active`) and radios (lit by the checked radio in their label).
 *
 * The click is caught on the way down, so the pill that was lit is still lit
 * when it is read; a page controller that moves `data-active` itself runs
 * after. Whatever the click loads waits for the slide to end before it
 * renders — a frame, the whole page, or a stream — so the server's markup
 * lands with the same pill lit in the same place.
 *
 * Clicking again mid-slide turns the one indicator around from wherever it
 * is, and a frame that arrived for an earlier click is dropped.
 */
export default class extends Controller<HTMLElement> {
  private indicator: HTMLElement | null = null;
  private animation: Animation | null = null;
  private sliding: Promise<void> | null = null;
  private clicks = 0;

  connect() {
    document.addEventListener("turbo:before-frame-render", this.holdRender);
    document.addEventListener("turbo:before-render", this.holdRender);
    document.addEventListener("turbo:before-stream-render", this.holdStream);
  }

  disconnect() {
    document.removeEventListener("turbo:before-frame-render", this.holdRender);
    document.removeEventListener("turbo:before-render", this.holdRender);
    document.removeEventListener("turbo:before-stream-render", this.holdStream);
  }

  slide(event: MouseEvent) {
    if (!isPlainClick(event)) return;

    const to = (event.target as Element).closest<HTMLElement>("a, button, label");
    const from = this.litPill();
    if (!to || to.parentElement !== this.element || to === from) return;

    this.clicks += 1;
    // A radio's label lights itself once the browser checks the radio.
    if (!to.matches("label")) {
      from?.removeAttribute("data-active");
      to.setAttribute("data-active", "");
    }

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced || typeof HTMLElement.prototype.animate !== "function") return;

    // Positions the track first, so every offset below is measured from it.
    this.element.setAttribute("data-pill-slide-sliding", "");
    const start = this.indicator ? this.indicatorBox() : from ? this.box(from) : { ...this.box(to), opacity: 0 };
    const indicator = this.indicator ?? this.createIndicator(to);
    this.animation?.cancel();

    const animation = indicator.animate([start, this.box(to)], {
      duration: 260,
      easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
      fill: "forwards",
    });
    this.animation = animation;
    const settle = () => {
      if (this.animation === animation) this.settle();
    };
    this.sliding = animation.finished.then(settle, settle);
  }

  // Read before the click lands, so a radio's previous choice is still checked.
  private litPill() {
    return (
      this.element.querySelector<HTMLElement>(":scope > [data-active]") ??
      this.element.querySelector("input:checked")?.closest<HTMLElement>("label") ??
      null
    );
  }

  private createIndicator(to: HTMLElement) {
    const indicator = document.createElement("span");
    indicator.className = "pill-slide-indicator";
    indicator.style.top = `${to.offsetTop}px`;
    indicator.style.height = `${to.offsetHeight}px`;
    this.element.prepend(indicator);
    this.indicator = indicator;
    return indicator;
  }

  // The lit pill's own background takes over, so a resize can't strand the indicator.
  private settle() {
    this.indicator?.remove();
    this.element.removeAttribute("data-pill-slide-sliding");
    this.indicator = null;
    this.animation = null;
    this.sliding = null;
  }

  private box(pill: HTMLElement) {
    return { transform: `translateX(${pill.offsetLeft}px)`, width: `${pill.offsetWidth}px`, opacity: 1 };
  }

  // Where the indicator is drawn right now, mid-slide included.
  private indicatorBox() {
    const indicator = this.indicator!.getBoundingClientRect();
    const track = this.element.getBoundingClientRect();
    const x = indicator.left - track.left - this.element.clientLeft;
    return { transform: `translateX(${x}px)`, width: `${indicator.width}px`, opacity: 1 };
  }

  // A frame or page render that would replace the track waits for the slide.
  // A frame that arrived before a newer click is skipped; the latest click's
  // frame renders when it comes.
  private holdRender = (event: Event) => {
    if (!this.sliding || !(event.target as Node).contains(this.element)) return;

    const detail = (event as CustomEvent).detail;
    if (event.type === "turbo:before-frame-render") {
      const clicksAtArrival = this.clicks;
      const render = detail.render;
      detail.render = (current: Element, next: Element) => {
        if (this.clicks === clicksAtArrival) render(current, next);
      };
    }

    event.preventDefault();
    this.whenSettled().then(() => detail.resume());
  };

  // Streams can't be paused, but Turbo awaits their render function.
  private holdStream = (event: Event) => {
    if (!this.sliding) return;

    const stream = event.target as Element & { targetElements: Element[] };
    if (!stream.targetElements.some((target) => target.contains(this.element))) return;

    const detail = (event as CustomEvent).detail;
    const render = detail.render;
    detail.render = async (element: Element) => {
      await this.whenSettled();
      return render(element);
    };
  };

  // A newer click replaces `sliding`, so wait for whichever slide is last.
  private async whenSettled() {
    while (this.sliding) await this.sliding;
  }
}

// A modified or non-primary click opens a new tab or window; this page stays put.
function isPlainClick(event: MouseEvent) {
  return event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}
